/** Ventana de cooldown por defecto entre sincronizaciones de una misma clave (24 h). */
const SYNC_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** Control de concurrencia y cooldown en memoria para servicios de sincronización.
 *  Evita llamadas duplicadas al API para una misma clave (apiRoute) dentro de la ventana de cooldown.
 *  No persiste entre recargas: al cerrar sesión o recargar el cooldown se reinicia.
 */
class ServiceSync {
  /** Instancia única. */
  static readonly instance = new ServiceSync();
  private constructor() {}

  /** Llamadas en curso por clave (dedupe de concurrencia). */
  private readonly _running = new Map<string, Promise<void>>();
  /** Última vez (ms) que se sincronizó cada clave (para el cooldown). */
  private readonly _lastFetchedAt = new Map<string, number>();

  /** Ejecuta fn si no hay una llamada en curso para key ni cooldown activo.
   *  @param key Identificador único (ej: apiRoute + scope).
   *  @param fn Función asíncrona que ejecuta la llamada al API.
   *  @param cooldownMs Ventana de cooldown para esta clave (default: el global de 24 h).
   *  @returns Promise que resuelve cuando fn completa o si ya estaba en curso/cooldown.
   */
  run(
    key: string,
    fn: () => Promise<void>,
    cooldownMs: number = SYNC_COOLDOWN_MS,
  ): Promise<void> {
    if (this._running.has(key)) return this._running.get(key)!;
    const last = this._lastFetchedAt.get(key) ?? 0;
    if (Date.now() - last < cooldownMs) return Promise.resolve();
    const p = fn()
      .then(() => { this._lastFetchedAt.set(key, Date.now()); })
      .finally(() => { this._running.delete(key); });
    this._running.set(key, p);
    return p;
  }

  /** Limpia el cooldown de key para forzar una nueva llamada en el próximo run(). */
  reset(key: string): void {
    this._lastFetchedAt.delete(key);
  }
}

/** Singleton de control de cooldown/concurrencia de sincronización. */
export const serviceSync = ServiceSync.instance;
